import {useCallback, useMemo} from 'react';
import {Partner} from '@/Pages/Partners/store/types';
import {Country} from '@/Pages/Countries/store/types';
import {ProductionCostBusProvidedValuesEntry} from '@/Pages/ProductionCost/store/types';
import {InvalidProductionCostBusScheduleCostFactorErrorExtensions} from '../../types';

export const useInvalidCostFactorHelpers = (
  busValues: ProductionCostBusProvidedValuesEntry[] | null,
  busPartners: Partner[] | null,
  countries: Country[] | null
) => {
  const logMissing = useCallback(
    (
      entity: string,
      error: InvalidProductionCostBusScheduleCostFactorErrorExtensions
    ) => {
      const stringifiedObj = JSON.stringify(error, null, 2);
      console.error(`Missing ${entity} for error: ${stringifiedObj}`);
    },
    []
  );

  // Create indexed Maps for O(1) lookups instead of O(n) array.find()
  const busValuesMap = useMemo(
    () => new Map(busValues?.map(bv => [bv.vehicleId, bv]) ?? []),
    [busValues]
  );

  const busPartnersMap = useMemo(
    () => new Map(busPartners?.map(bp => [bp.id, bp]) ?? []),
    [busPartners]
  );

  const countriesMap = useMemo(
    () => new Map(countries?.map(c => [c.id, c]) ?? []),
    [countries]
  );

  // Helper to get vehicle settings from error
  const getVehicleSettings = useCallback(
    (error: InvalidProductionCostBusScheduleCostFactorErrorExtensions) => {
      const vId = error.bus?.vehicleId;
      if (!vId) {
        logMissing('vehicle id', error);
        return null;
      }

      const vehicleSettings = busValuesMap.get(vId);
      if (!vehicleSettings) {
        logMissing('bus values', error);
        return null;
      }

      return vehicleSettings;
    },
    [busValuesMap, logMissing]
  );

  // Helper to get vehicle settings and bus partner from error
  const getVehicleContext = useCallback(
    (error: InvalidProductionCostBusScheduleCostFactorErrorExtensions) => {
      const vehicleSettings = getVehicleSettings(error);
      if (!vehicleSettings) return null;

      if (!vehicleSettings.busPartnerId) {
        logMissing('bus partner id', error);
        return null;
      }

      const busPartner = busPartnersMap.get(vehicleSettings.busPartnerId);
      if (!busPartner) {
        logMissing('bus partner', error);
        return null;
      }

      return {vehicleSettings, busPartner};
    },
    [getVehicleSettings, busPartnersMap, logMissing]
  );

  return {
    logMissing,
    busValuesMap,
    busPartnersMap,
    countriesMap,
    getVehicleSettings,
    getVehicleContext
  };
};
